import type { UseFetchOptions } from "nuxt/app";
import { useEtcStore } from "~/store/etcStore";
import { useUserStore } from "~/store/userStore";

// 동시 API 호출을 제어하기 위한 Map (병렬 처리 지원)
const pendingRequests = new Map<string, Promise<any>>();

export async function useCustomFetch<T>(
  url: string | (() => string),
  options: UseFetchOptions<T> = {}
) {
  const config = useRuntimeConfig();
  const userStore = useUserStore();
  const etcStore = useEtcStore();
  const userToken = useCookie("userToken");

  // 토큰 확보
  if (!userStore.apiTokenInfo?.accessToken || !userToken.value) {
    await userStore.refreshToken();
  }
  const accessToken = userStore.apiTokenInfo?.accessToken ?? "";

  // URL 키 생성 (동시 요청 제어용)
  const urlKey = typeof url === 'string' ? url : 'function';
  
  // 특정 API URL에 대한 설정
  const getApiConfig = (url: string) => {
    // 에러 응답을 그대로 받아야 하는 API들
    const noRetryApis = [
      'work/validationserial',
      'work/works/detail',
      'work/works/bfdetail',
      'v1/bpm',
      'bpm/excel',
      'bpm/excel/template',
      'system/bpm'
    ];
    
    // 재시도하지 않아야 하는 API들
    const skipRetryApis = [
      'work/validationserial',
      'work/works/detail',
      'work/works/bfdetail',
      'v1/bpm',
      'bpm/excel',
      'bpm/excel/template',
      'system/bpm'
    ];
    
    const shouldReturnErrorResponse = noRetryApis.some(api => url.includes(api));
    const shouldSkipRetry = skipRetryApis.some(api => url.includes(api));
    
    return {
      shouldReturnErrorResponse,
      shouldSkipRetry
    };
  };
  
  const apiConfig = getApiConfig(urlKey);
  
  // query와 params를 포함한 고유한 요청 키 생성
  const createRequestKey = (): string => {
    let key = urlKey;
    
    // query 파라미터가 있다면 키에 포함
    if (options.query && Object.keys(options.query).length > 0) {
      const queryString = JSON.stringify(options.query);
      key += `?${queryString}`;
    }
    
    // params 파라미터가 있다면 키에 포함
    if (options.params && Object.keys(options.params).length > 0) {
      const paramsString = JSON.stringify(options.params);
      key += `&${paramsString}`;
    }
    
    // body가 있다면 키에 포함 (POST, PUT 등)
    if (options.body) {
      const bodyString = JSON.stringify(options.body);
      key += `#${bodyString}`;
    }
    
    return key;
  };
  
  const requestKey = createRequestKey();
  
  // 이미 진행 중인 동일한 요청(URL + query + params + body)이 있다면 그것을 반환
  if (pendingRequests.has(requestKey)) {
    console.log(`🔄 동일한 요청이 진행 중입니다: ${requestKey}`);
    return pendingRequests.get(requestKey);
  }

  // 개발 중 "Reloading server..." 방어용 재시도 (최대 3번)
  const MAX_RETRY = apiConfig.shouldSkipRetry ? 0 : 3;
  
  // 503 에러에 대한 특별한 재시도 전략
  const createRetryPromise = async (): Promise<any> => {
    // Blob 응답인지 확인
    const isBlobResponse = (options as any).responseType === 'blob';
    const requestTimeout = (() => {
      if (urlKey.includes('report/construction/jobs')) {
        return isBlobResponse ? 180_000 : 30_000;
      }
      if (isBlobResponse && urlKey.includes('report/construction/pdf')) {
        return 120_000;
      }
      return 20_000;
    })();
    
    for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
      try {
        const res = await useFetch<T>(url, {
          baseURL: config.public.apiBase,
          method: (options.method as any) ?? "GET",
          query: options.query,
          params: options.params,
          body: options.body,
          // 동일 요청 재시도 시 useFetch 캐시/중복요청에 걸리지 않도록 key/header 변조
          key: `${requestKey}:${attempt}:${Date.now()}`,
          headers: {
            Accept: isBlobResponse ? "*/*" : "application/json",
            Authorization: `Bearer ${accessToken}`,
            ...(options.headers as Record<string, string>),
            "x-retry-attempt": String(attempt)
          },
          // Blob 응답 처리
          ...(isBlobResponse ? { responseType: 'blob' as any } : {}),
          // oFetch 자동 재시도 비활성화 (중복 API 호출 방지)
          retry: 0,
          retryDelay: 0,
          timeout: requestTimeout,
          // HTML 응답 감지 및 오류 처리
          onResponse({ response }) {
            // HTML 응답인지 확인 (서버 재시작 등)
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
              const responseText = response._data;
              if (typeof responseText === 'string' && responseText.includes('<!DOCTYPE html>')) {
                console.error('❌ 서버에서 HTML 응답을 받았습니다. 서버가 재시작되었을 수 있습니다.');
                throw new Error('SERVER_RELOADING');
              }
            }
          },
          // 응답 오류 처리
          onResponseError({ response, error }) {
            // 특정 API는 에러 응답을 그대로 반환
            console.log(apiConfig.shouldReturnErrorResponse);
            if (apiConfig.shouldReturnErrorResponse) {
              console.log(`📋 ${urlKey}: 에러 응답을 그대로 반환합니다.`);
              return response;
            }
        
            console.error('❌ API 응답 오류:', {
              url: requestKey,
              status: response?.status,
              statusText: response?.statusText,
              error: error?.message
            });
            
            // 503 에러에 대한 특별한 처리
            if (response?.status === 503) {
              console.warn('⚠️ 503 Server Unavailable 에러 발생. 재시도 전략을 적용합니다.');
              throw new Error('SERVER_UNAVAILABLE_503');
            }
            
            // HTML 응답인지 확인
            if (response && response._data) {
              const responseText = response._data;
              if (typeof responseText === 'string' && responseText.includes('<!DOCTYPE html>')) {
                console.error('❌ 서버에서 HTML 응답을 받았습니다. 서버가 재시작되었을 수 있습니다.');
                throw new Error('SERVER_RELOADING');
              }
            }
            
            throw error;
          },
          // 네트워크 오류 처리
          onRequestError({ error }) {
            console.error('❌ API 요청 오류:', {
              url: requestKey,
              error: error?.message
            });
            throw error;
          }
        });

        // 개발 서버 재기동 HTML이 섞여 들어왔는지 검사 (기존 로직 강화)
        const maybeHtml = typeof res.data.value === "string" ? (res.data.value as unknown as string) : "";
        const looksLikeReloadHtml =
          maybeHtml.includes("<title>Reloading server") ||
          maybeHtml.includes("Check console logs for more information") ||
          maybeHtml.includes("<!DOCTYPE html>") ||
          maybeHtml.includes("<html") ||
          maybeHtml.includes("<head>") ||
          maybeHtml.includes("<body>");

        const failed = !!res.error.value || looksLikeReloadHtml;

        if (!failed) return res;
        if (attempt === MAX_RETRY) return res; // 마지막 시도는 그대로 반환

        // 특정 API는 재시도하지 않음
        if (apiConfig.shouldSkipRetry) {
          console.log(`📋 ${urlKey}: 재시도하지 않고 에러 응답을 반환합니다.`);
          return res;
        }

        // HTML 응답 감지 시 더 긴 대기 시간
        if (looksLikeReloadHtml) {
          console.log(`🔄 HTML 응답 감지 (${attempt + 1}/${MAX_RETRY + 1}). 서버 재시작 대기 중...`);
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); // 1초, 2초, 3초
        } else {
          // 일반 오류 시 기존 백오프
          await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
        }

      } catch (error: any) {
        console.error(`❌ API 호출 시도 ${attempt + 1}/${MAX_RETRY + 1} 실패:`, error);
        
        // 특정 API는 재시도하지 않음
        if (apiConfig.shouldSkipRetry) {
          console.log(`📋 ${urlKey}: 재시도하지 않고 에러를 반환합니다.`);
          throw error;
        }
        
        // 503 에러에 대한 특별한 재시도 전략 (최대 3번)
        if (error?.message === 'SERVER_UNAVAILABLE_503') {
          if (attempt === MAX_RETRY) {
            console.error('❌ 503 에러 최대 재시도 횟수 도달. 오류를 반환합니다.');
            throw error;
          }
          console.log(`🔄 503 Server Unavailable 감지 (${attempt + 1}/${MAX_RETRY + 1}). 서버 부하 대기 중...`);
          // 503 에러는 서버 부하나 일시적 문제일 가능성이 높으므로 더 긴 대기 시간 적용
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); // 2초, 4초, 6초
          continue; // 다음 시도로 진행
        }
        
        // SERVER_RELOADING 오류 시 더 긴 대기 시간
        if (error?.message === 'SERVER_RELOADING') {
          if (attempt === MAX_RETRY) {
            console.error('❌ 서버 재시작 감지 최대 재시도 횟수 도달. 오류를 반환합니다.');
            throw error;
          }
          console.log(`🔄 서버 재시작 감지 (${attempt + 1}/${MAX_RETRY + 1}). 대기 중...`);
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        } else if (attempt === MAX_RETRY) {
          // 마지막 시도에서 오류 발생 시 그대로 반환
          console.error('❌ 최대 재시도 횟수 도달. 오류를 반환합니다.');
          throw error;
        } else {
          // 일반 오류 시 기존 백오프
          await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
        }
      }
    }

    // 타입 가드를 위해 (여기 오기 어렵습니다)
    return await useFetch<T>(url, { baseURL: config.public.apiBase });
  };

  // 요청 실행 및 pendingRequests에 저장
  const requestPromise = createRetryPromise();
  pendingRequests.set(requestKey, requestPromise);

  try {
    const result = await requestPromise;
    return result;
  } finally {
    // 요청 완료 후 정리
    pendingRequests.delete(requestKey);
  }
}
